<template>
  <div id="user-list">
    <v-card>
      <v-tabs v-model="activeTab" show-arrows>
        <v-tab>{{ $t("Käyttäjät") }}</v-tab>
        <v-tab>{{ $t("Pääkäyttäjät") }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card-title> {{ $t("Käyttäjät") }} </v-card-title>
          <!-- actions -->
          <div class="d-flex">
            <v-spacer></v-spacer>
            <v-btn color="secondary" class="mb-4 mr-4" @click="invite">
              {{ $t("Kutsu uudet käyttäjät") }}
            </v-btn>
            <v-btn color="primary" class="mb-4 mr-4" :to="{ name: 'admin-user-form' }">
              {{ $t("Uusi käyttäjä") }}
            </v-btn>
          </div>

      <v-card-text class="d-flex align-center flex-wrap pb-0">
        <v-row>
          <v-col cols="6">
            <v-text-field :disabled="selectedUserFilter !== 'all'" :label="$t('Hae')" dense outlined clearable
              v-model="searchFilter"></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-select dense outlined item-value="value" item-text="title" :items="userFilters"
              v-model="selectedUserFilter" @change="filterChanged">
            </v-select>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text>
        <template v-if="getActiveFilter().type == 'group'">
          <v-switch @change="filterChanged" v-model="showGroupAdmins" :label="$t('Näytä ryhmän ylläpitäjät')">
          </v-switch>
        </template>
      </v-card-text>

      <v-data-table :headers="tableColumns" :items="users" :server-items-length="totalCount" :items-per-page="pageSize"
        :options.sync="options" :loading="loading" mobile-breakpoint="0" :footer-props="{
          'items-per-page-options': [pageSize],
        }">
        <template #[`item.picture`]="{ item }">
          <v-avatar v-if="item.read_only">
            <img style="width: 28px; height: 28px" v-if="getUserImage(item, 'thumbnail')"
              :src="getUserImage(item, 'thumbnail')" />
          </v-avatar>
          <v-avatar v-else style="cursor: pointer" @click="editUserImage(item)">
            <img style="width: 28px; height: 28px" v-if="getUserImage(item, 'thumbnail')"
              :src="getUserImage(item, 'thumbnail')" />
            <v-icon size="24" v-else> {{ icons.mdiCameraPlusOutline }} </v-icon>
          </v-avatar>
        </template>

        <template #[`item.last_name`]="{ item }">
          <div class="d-flex align-center">
            <router-link :to="{ name: 'admin-user-form', params: { id: item.id } }"
              class="font-weight-medium text-decoration-none">
              {{ item.last_name }}
            </router-link>
          </div>
        </template>

        <template #[`item.first_name`]="{ item }">
          <div class="d-flex align-center">
            <router-link :to="{ name: 'admin-user-form', params: { id: item.id } }"
              class="font-weight-medium text-decoration-none">
              {{ item.first_name }}
            </router-link>
          </div>
        </template>

        <template #[`item.phone`]="{ item }">
          <template v-if="item.phone">
            <v-icon v-if="item.show_phone" color="success">
              {{ icons.mdiEye }}
            </v-icon>
            <v-icon v-else color="error"> {{ icons.mdiEyeOffOutline }} </v-icon>&nbsp;&nbsp;
            <a :href="'tel:' + item.phone">{{ item.phone }}</a>
          </template>
        </template>

        <template #[`item.email`]="{ item }">
          <template v-if="item.email">
            <v-icon v-if="item.show_email" color="success">
              {{ icons.mdiEye }}</v-icon>
            <v-icon v-else color="error"> {{ icons.mdiEyeOffOutline }} </v-icon>&nbsp;&nbsp;
            <a :href="'mailto:' + item.email">{{ item.email }}</a>
          </template>
        </template>

        <template #[`item.is_active`]="{ item }">
          <v-icon :color="item.is_active ? 'success' : 'error'">
            <template v-if="item.is_active">
              {{ icons.mdiCheck }}
            </template>
            <template v-else>
              {{ icons.mdiBlockHelper }}
            </template>
          </v-icon>
        </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item>
          <div class="mt-3">
            <main-users-panel />
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <v-dialog :key="editProfileImageUserId" v-model="dialog" max-width="440px">
      <v-card>
        <profile-image-form :key="editProfileImageUserId" v-if="editProfileImageUserId"
          :person-name="editProfileImagePersonName" :current-image="editProfileImageCurrentImage"
          :user-id="editProfileImageUserId" :width="400" :height="400" @updated="pictureUpdated"></profile-image-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="newUsersDialog" max-width="720px" persistent>
      <div style="position: relative;">
        <v-avatar size="32" @click="(newUsersDialog = false)"
          style="cursor: pointer; position: absolute; right: 10px; top: 10px; z-index: 999; background-color: #3e3d3d;">
          <v-icon color="white" size="24">{{ icons.mdiClose }}</v-icon>
        </v-avatar>
        <invite-users :key="newUsersDialogKey"></invite-users>
      </div>
    </v-dialog>
  </div>
</template>


<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import adminApi from "@/api/admin";
import {
  mdiCheckboxMarkedCircle,
  mdiCloseCircle,
  mdiPlus,
  mdiEye,
  mdiEyeOffOutline,
  mdiCheck,
  mdiBlockHelper,
  mdiClose,
  mdiCameraPlusOutline,
} from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import profileImageForm from "./components/profile_image_form.vue";
import inviteUsers from './components/invite_users.vue';
import mainUsersPanel from './components/main_users_panel.vue';
import useAuth from "@/useAuth";

export default {
  components: {
    profileImageForm,
    inviteUsers,
    mainUsersPanel,
  },
  setup() {
    const activeTab = ref(0);
    const users = ref([]);
    const newUsers = ref([]);
    const searchFilter = ref("");

    const totalCount = ref(0);
    const pageSize = 100;
    const { user } = useAuth();

    const page = ref(1);
    const options = ref({});
    const loading = ref(false);
    const error = ref("");
    const selectedUserFilter = ref("all");
    const showGroupAdmins = ref(false);
    const newUsersDialogKey = ref(new Date().toString());

    const { t } = useTranslations();

    const dialog = ref(false);
    const newUsersDialog = ref(false);
    const editProfileImagePersonName = ref("");
    const editProfileImageUserId = ref(null);
    const editProfileImageCurrentImage = ref(null);

    const tableColumns = [
      { text: "", value: "picture", sortable: false },
      {
        text: t("Sukunimi").toUpperCase(),
        value: "last_name",
        sortable: false,
      },
      {
        text: t("Etunimi").toUpperCase(),
        value: "first_name",
        sortable: false,
      },
      {
        text: t("Työtehtävä").toUpperCase(),
        value: "title",
        sortable: false,
      },
      {
        text: t("Puhelin").toUpperCase(),
        value: "phone",
        sortable: false,
      },
      {
        text: t("Sähköposti").toUpperCase(),
        value: "email",
        sortable: false,
      },
      {
        text: t("Aktiivinen").toUpperCase(),
        value: "is_active",
        sortable: false,
      },
    ];

    // fetch data
    const fetchUsers = () => {
      const activeFilter = getActiveFilter();

      switch (activeFilter.type) {
        case "all":
          fetchAllUsers();
          break;
        case "mainusers":
          fetchMainUsers();
          break;
        case "group":
          if (showGroupAdmins.value) {
            fetchGroupAdmins(activeFilter.value);
          } else {
            fetchGroupMembers(activeFilter.value);
          }
          break;
        default:
          break;
      }
    };

    const fetchNewUsers = async => {

    }

    const fetchAllUsers = async () => {
      loading.value = true;
      const response = await adminApi.getUsers(page.value, pageSize, searchFilter.value);

      if (response.ok) {
        users.value = response.data.results;
        totalCount.value = response.data.count;
      }

      loading.value = false;
    };

    const fetchGroupMembers = async (groupId) => {
      loading.value = true;
      const response = await adminApi.getGroupMembers(
        groupId,
        page.value,
        pageSize
      );

      if (response.ok) {
        users.value = response.data.results;
        totalCount.value = response.data.count;
      }

      loading.value = false;
    };

    const fetchGroupAdmins = async (groupId) => {
      loading.value = true;
      const response = await adminApi.getGroupAdmins(
        groupId,
        page.value,
        pageSize
      );

      if (response.ok) {
        users.value = response.data.results;
        totalCount.value = response.data.count;
      }

      loading.value = false;
    };

    const fetchMainUsers = async () => {
      loading.value = true;
      const response = await adminApi.getTenantAdmins(page.value, pageSize);

      if (response.ok) {
        users.value = response.data.results;
        totalCount.value = response.data.count;
      }

      loading.value = false;
    };

    const editUserImage = (user) => {
      editProfileImageUserId.value = user.id;
      editProfileImagePersonName.value = user.first_name + " " + user.last_name;
      editProfileImageCurrentImage.value = getUserImage(user, "full_hd");

      dialog.value = true;
    };

    const pictureUpdated = (data) => {
      dialog.value = false;
      fetchUsers();
    };

    const getUserImage = (u, size) => {
      if (!u.picture || !Object.keys(u.picture).length) {
        return null;
      }
      return u.picture[size];
    };

    const getActiveFilter = () => {
      const filter = userFilters.value.find(
        (f) => f.value == selectedUserFilter.value
      );
      return filter;
    };

    const filterChanged = () => {
      options.value.page = 1;
      page.value = options.value.page;
      fetchUsers();
    };

    const userFilters = computed(() => {
      1;
      let f = [
        {
          value: "all",
          title: t("Kaikki käyttäjät"),
          type: "all",
        },
        {
          value: "mainusers",
          title: t("Yrityksen pääkäyttäjät"),
          type: "mainusers",
        },
      ];
      f = f.concat(
        f,
        user.value.admin_groups
          .filter((g) => g.group_type != 1)
          .map((g) => {
            return {
              value: g.id,
              title: `${g.name} (Ryhmä)`,
              type: "group",
            };
          })
      );

      return f;
    });

    const invite = async () => {
      newUsersDialogKey.value = new Date().toLocaleString();
      newUsersDialog.value = true;
    }

    watch(options, () => {
      page.value = options.value.page;
      fetchUsers();
    });

    let searchTimeout = null;
    watch(searchFilter, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        if (searchFilter.value) {
          if (searchFilter.value.length > 2) {
            filterChanged();
          }
        }
        else {
          filterChanged();
        }
      }, 600);
    });


    return {
      activeTab,
      users,
      totalCount,
      tableColumns,
      options,
      loading,
      pageSize,
      getDateTime,
      editUserImage,
      getUserImage,
      pictureUpdated,
      showGroupAdmins,
      getActiveFilter,
      filterChanged,
      selectedUserFilter,
      editProfileImageUserId,
      editProfileImageCurrentImage,
      editProfileImagePersonName,
      dialog,
      newUsersDialog,
      userFilters,
      newUsersDialogKey,
      searchFilter,
      invite,
      icons: {
        mdiCheckboxMarkedCircle,
        mdiCloseCircle,
        mdiCheck,
        mdiBlockHelper,
        mdiPlus,
        mdiEye,
        mdiEyeOffOutline,
        mdiCameraPlusOutline,
        mdiClose,
      },
      t,
    };
  },
};
</script>

<style></style>
